import React, { useEffect, useState, useCallback, useRef } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { EmailConfigSection } from '../components/EmailConfigSection';
import { SupportEmailConfigSection } from '../components/SupportEmailConfigSection';
import { MarketingIntegrationsSection } from '../components/MarketingIntegrationsSection';
import {
  RefreshCw, Plug, Unplug, Play, CheckCircle, XCircle,
  AlertCircle, ShoppingBag, Globe, FileText, ChevronDown, ChevronUp,
  TrendingUp, Upload, BarChart2, Download, Image, Search, X as XIcon,
} from 'lucide-react';

// ── Platform logos (inline SVG icons) ────────────────────────
function PrestashopIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#DF0067" />
      <text x="16" y="22" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">PS</text>
    </svg>
  );
}
function ShopifyIcon({ size = 24 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" fill="none">
      <rect width="32" height="32" rx="6" fill="#96BF48" />
      <text x="16" y="22" textAnchor="middle" fill="white" fontSize="14" fontWeight="bold">SH</text>
    </svg>
  );
}

// ── Sync status badge ─────────────────────────────────────────
function SyncBadge({ status, productCount }) {
  if (!status) return <span className="text-gray-600 text-xs">No sincronizado</span>;
  const s = status.status;
  if (s === 'syncing') {
    // Detectar si el sync lleva >8 min sin actualizar updated_at (posible atasco)
    const lastUpdate = status.updated_at ? new Date(status.updated_at) : null;
    const minsStale = lastUpdate ? Math.floor((Date.now() - lastUpdate) / 60000) : null;
    const isStuck = minsStale !== null && minsStale > 8;
    const streamRead = status.stream_read || 0;
    const label = streamRead > 0
      ? `Leyendo catálogo… ${streamRead.toLocaleString()} leídos`
      : status.products_synced > 0
        ? `Sincronizando (${status.products_synced.toLocaleString()} productos)`
        : 'Leyendo catálogo del módulo…';
    return (
      <span className={`flex items-center gap-1.5 text-xs ${isStuck ? 'text-red-400' : 'text-amber-400'}`}>
        <RefreshCw size={12} className="animate-spin" />
        {isStuck ? `Posible atasco (${minsStale} min sin avance) — considera cancelar y reintentar` : label}
      </span>
    );
  }
  if (s === 'completed') return (
    <span className="flex items-center gap-1.5 text-emerald-400 text-xs">
      <CheckCircle size={12} /> {productCount?.toLocaleString() || status.products_synced} productos indexados
    </span>
  );
  if (s === 'error') return (
    <span className="flex items-center gap-1.5 text-red-400 text-xs">
      <XCircle size={12} /> Error: {status.error?.slice(0, 60)}
    </span>
  );
  return <span className="text-gray-500 text-xs">—</span>;
}

// ── PrestaShop Card ───────────────────────────────────────────
function PrestashopCard({ data, onRefresh }) {
  const [showForm, setShowForm] = useState(false);
  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');
  const [isError403, setIsError403] = useState(false);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  // Ref to track active polling interval — prevents multiple intervals stacking up
  // when the user clicks Sync / Cancel / Sync in quick succession.
  const pollIntervalRef = React.useRef(null);

  // Feed preview modal state
  const [showFeedModal, setShowFeedModal] = useState(false);
  const [feedModalTab, setFeedModalTab] = useState('feed'); // 'feed' | 'db'
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedData, setFeedData] = useState(null);
  const [feedError, setFeedError] = useState(null);
  const [feedSearch, setFeedSearch] = useState('');
  const [feedSearchInput, setFeedSearchInput] = useState('');
  const [dbData, setDbData] = useState(null);
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState(null);

  const loadFeedPreview = async (search = '') => {
    setFeedLoading(true);
    setFeedError(null);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (search) params.set('search', search);
      const res = await api.get(`/api/connectors/prestashop/feed-preview?${params.toString()}`);
      setFeedData(res.data);
    } catch (e) {
      setFeedError(e.response?.data?.detail || 'Error al descargar el feed');
    } finally {
      setFeedLoading(false);
    }
  };

  const loadDbInspect = async (search = '') => {
    if (!search) return;
    setDbLoading(true);
    setDbError(null);
    try {
      const res = await api.get(`/api/connectors/prestashop/product-inspect?q=${encodeURIComponent(search)}`);
      setDbData(res.data);
    } catch (e) {
      setDbError(e.response?.data?.detail || 'Error consultando la BD');
    } finally {
      setDbLoading(false);
    }
  };

  const isConfigured = data?.configured;
  const syncStatus = data?.sync;
  const isSyncing = syncStatus?.status === 'syncing';
  const [showLangModal, setShowLangModal] = useState(false);

  const handleConfigure = async (force = false) => {
    if (!url.trim() || !apiKey.trim()) {
      setMsg('Rellena todos los campos');
      return;
    }
    setSaving(true);
    setIsError403(false);
    try {
      const res = await api.post('/api/connectors/prestashop/configure', {
        shop_url: url.trim(),
        api_key: apiKey.trim(),
        force,
      });
      setMsg(res.data.message);
      setShowForm(false);
      setIsError403(false);
      onRefresh();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error de conexión';
      setMsg(detail);
      if (detail.includes('403') || detail.includes('permiso')) {
        setIsError403(true);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSync = async (reset = false, forceHashes = false) => {
    setSyncing(true);
    try {
      const params = new URLSearchParams();
      if (reset) params.set('reset', 'true');
      if (forceHashes) params.set('force_hashes', 'true');
      const qs = params.toString() ? `?${params.toString()}` : '';
      await api.post(`/api/connectors/prestashop/sync${qs}`);
      if (forceHashes) setMsg('Resync forzado iniciado — todos los textos se actualizarán aunque el hash coincida');
      else setMsg(reset ? 'Sincronización completa iniciada' : 'Sincronización iniciada');
      let polls = 0;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(async () => {
        polls++;
        onRefresh();
        if (polls > 60) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      }, 3000);
      setTimeout(() => { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }, 300000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncImages = async (all = false) => {
    setSyncing(true);
    try {
      await api.post(`/api/connectors/prestashop/sync-images${all ? '?all=true' : ''}`);
      setMsg('Sincronización de imágenes iniciada');
      let polls = 0;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(async () => {
        polls++;
        onRefresh();
        if (polls > 80) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      }, 3000);
      setTimeout(() => { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }, 300000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al sincronizar imágenes');
    } finally {
      setSyncing(false);
    }
  };

  const handleCancelSync = async () => {
    if (!window.confirm('¿Detener la sincronización en curso? Los productos ya importados se conservan.')) return;
    try {
      await api.post('/api/connectors/prestashop/sync/cancel');
      setMsg('Cancelación solicitada. El sync se detendrá en unos segundos.');
      let polls = 0;
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = setInterval(async () => {
        polls++;
        onRefresh();
        if (polls > 20) { clearInterval(pollIntervalRef.current); pollIntervalRef.current = null; }
      }, 3000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al cancelar');
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar PrestaShop? Esto no borrará los productos indexados.')) return;
    await api.delete('/api/connectors/prestashop/disconnect');
    onRefresh();
  };

  const handleDiagnose = async () => {
    setDiagnosing(true);
    setDiagnosis(null);
    setMsg('');
    try {
      const res = await api.get('/api/connectors/prestashop/diagnose');
      setDiagnosis(res.data);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al ejecutar el diagnóstico');
    } finally {
      setDiagnosing(false);
    }
  };

  return (
    <div data-testid="prestashop-card" className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <PrestashopIcon size={36} />
          <div>
            <h3 className="text-white font-semibold">PrestaShop</h3>
            <p className="text-gray-500 text-xs mt-0.5">REST API · Catálogo de productos</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          isConfigured ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-700 text-gray-400'
        }`}>
          {isConfigured ? <><Plug size={10} /> Conectado</> : <><Unplug size={10} /> No conectado</>}
        </div>
      </div>

      {isConfigured && (
        <div className="mb-4">
          <p className="text-gray-500 text-xs mb-1">Tienda</p>
          <p className="text-gray-300 text-sm font-mono">{data.shop_url}</p>
          <div className="mt-2">
            <SyncBadge status={syncStatus} productCount={data.product_count} />
          </div>
          {syncStatus?.completed_at && (
            <p className="text-gray-600 text-xs mt-1">
              Última sync: {new Date(syncStatus.completed_at).toLocaleString('es')}
            </p>
          )}

          {/* ── Multistore + idiomas detectados ───────────── */}
          {Array.isArray(data.detected_shops) && data.detected_shops.length > 0 && (
            <div data-testid="ps-multistore-info" className="mt-3 bg-gray-800/40 border border-gray-700/60 rounded-xl p-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-gray-300">
                  {data.multistore
                    ? `Multistore detectado (${data.detected_shops.length} tiendas)`
                    : 'Tienda única'}
                </p>
                <div className="flex items-center gap-2">
                  {Array.isArray(data.detected_languages) && data.detected_languages.length > 0 && (
                    <p className="text-[10px] text-gray-500">
                      {data.detected_languages.length} {data.detected_languages.length === 1 ? 'idioma' : 'idiomas'}
                    </p>
                  )}
                  <button
                    data-testid="ps-manage-domains-btn"
                    onClick={() => setShowLangModal(true)}
                    className="text-[10px] text-indigo-300 hover:text-indigo-200 underline"
                  >
                    Gestionar
                  </button>
                </div>
              </div>
              <div className="space-y-1.5">
                {data.detected_shops.map((s) => (
                  <div key={s.id_shop} className="flex items-center justify-between text-xs gap-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-mono text-gray-500 shrink-0">#{s.id_shop}</span>
                      <span className="text-gray-300 truncate">{s.name}</span>
                      {s.domain && (
                        <span className="text-gray-500 font-mono truncate hidden sm:inline">
                          {s.domain}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-1 shrink-0 justify-end">
                      {(Array.isArray(s.active_lang_isos) && s.active_lang_isos.length > 0
                        ? s.active_lang_isos
                        : (s.default_lang_iso ? [s.default_lang_iso] : [])
                      ).map((iso) => (
                        <span
                          key={iso}
                          className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-mono ${
                            iso === s.default_lang_iso
                              ? 'bg-indigo-500/15 text-indigo-300'
                              : 'bg-gray-700/60 text-gray-400'
                          }`}
                          title={iso === s.default_lang_iso ? 'idioma por defecto' : 'idioma secundario'}
                        >
                          {iso}{iso === s.default_lang_iso ? '★' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {Array.isArray(data.detected_languages) && data.detected_languages.length > 0 && (
                <div className="mt-2 pt-2 border-t border-gray-700/60">
                  <p className="text-[10px] text-gray-500 mb-1">Idiomas indexados</p>
                  <div className="flex flex-wrap gap-1">
                    {data.detected_languages.map((iso) => (
                      <span
                        key={iso}
                        className={`px-1.5 py-0.5 rounded text-[10px] font-mono uppercase ${
                          iso === data.default_language
                            ? 'bg-emerald-500/15 text-emerald-300'
                            : 'bg-gray-700/60 text-gray-400'
                        }`}
                      >
                        {iso}{iso === data.default_language ? ' ★' : ''}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Toast message */}
      {msg && (
        <div className={`mb-3 text-xs rounded-lg px-3 py-2 border ${
          isError403
            ? 'text-orange-300 bg-orange-500/10 border-orange-500/20'
            : 'text-amber-300 bg-amber-500/10 border-amber-500/20'
        }`}>
          {msg}
        </div>
      )}

      {diagnosis && (
        <div data-testid="ps-diagnosis-panel" className="mb-3 text-xs rounded-lg px-3 py-3 border border-gray-700 bg-gray-800/60 space-y-1.5">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-white">Diagnóstico del feed</span>
            <button onClick={() => setDiagnosis(null)} className="text-gray-500 hover:text-white">✕</button>
          </div>
          <div className="text-gray-300">
            IP de salida de este servidor (whitelist en tu firewall):{' '}
            <span data-testid="ps-egress-ip" className="font-mono text-amber-300 select-all">{diagnosis.egress_ip || 'desconocida'}</span>
          </div>
          <div className="text-gray-400">
            Feed META: <span className={diagnosis.meta?.ok ? 'text-green-400' : 'text-red-400'}>
              {diagnosis.meta?.ok ? `OK (${diagnosis.meta?.shops_count} tiendas)` : `FALLO (${diagnosis.meta?.status || diagnosis.meta?.error || '—'})`}
            </span>
          </div>
          <div className="text-gray-400">
            Datos: <span className={diagnosis.data_sample?.lines_sampled > 0 ? 'text-green-400' : 'text-red-400'}>
              {diagnosis.data_sample?.lines_sampled > 0
                ? `OK (${diagnosis.data_sample.lines_sampled} líneas en ${diagnosis.data_sample.elapsed_s}s)`
                : `FALLO (${diagnosis.data_sample?.status || diagnosis.data_sample?.error || '—'})`}
            </span>
          </div>
          <div className={`pt-1 ${diagnosis.feed_reachable_from_server ? 'text-green-300' : 'text-red-300'}`}>
            {diagnosis.verdict}
          </div>
        </div>
      )}

      {/* Configure form */}
      {showForm && (
        <div data-testid="prestashop-config-form" className="mb-4 space-y-3 bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div>
            <label className="text-gray-400 text-xs block mb-1">URL de la tienda</label>
            <input
              data-testid="ps-url-input"
              type="text"
              value={url}
              onChange={e => { setUrl(e.target.value); setIsError403(false); setMsg(''); }}
              placeholder="https://mitienda.com"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 outline-none focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="text-gray-400 text-xs block mb-1">API Key de PrestaShop</label>
            <input
              data-testid="ps-apikey-input"
              type="password"
              value={apiKey}
              onChange={e => { setApiKey(e.target.value); setIsError403(false); setMsg(''); }}
              placeholder="ABCDEF123456..."
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 outline-none focus:border-indigo-500"
            />
            <p className="text-gray-600 text-xs mt-1">
              Panel Admin PrestaShop → Parámetros avanzados → Webservice → Añadir clave
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Button
              data-testid="ps-save-btn"
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={() => handleConfigure(false)}
              disabled={saving}
            >
              {saving ? <RefreshCw size={12} className="animate-spin mr-1" /> : null}
              Conectar
            </Button>
            {isError403 && (
              <Button
                data-testid="ps-force-save-btn"
                size="sm"
                className="bg-orange-600 hover:bg-orange-700 text-white text-xs"
                onClick={() => handleConfigure(true)}
                disabled={saving}
                title="Guarda las credenciales sin verificar la conexión. Útil si tu tienda restringe IPs."
              >
                Guardar de todas formas
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-gray-500" onClick={() => { setShowForm(false); setMsg(''); setIsError403(false); }}>
              Cancelar
            </Button>
          </div>
          {isError403 && (
            <p className="text-orange-400/70 text-xs">
              Si tus credenciales son correctas pero hay restricción de IP, usa "Guardar de todas formas".
            </p>
          )}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {!isConfigured ? (
          <Button
            data-testid="ps-configure-btn"
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => setShowForm(true)}
          >
            <Plug size={13} className="mr-1.5" /> Configurar
          </Button>
        ) : (
          <>
            <Button
              data-testid="ps-sync-btn"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => handleSync(false)}
              disabled={isSyncing || syncing}
              title="Sincronización rápida: nuevos productos, precios y desactivados"
            >
              {isSyncing ? <RefreshCw size={12} className="animate-spin mr-1.5" /> : <Play size={12} className="mr-1.5" />}
              {isSyncing ? 'Sincronizando...' : 'Sincronizar'}
            </Button>
            <Button
              data-testid="ps-full-sync-btn"
              size="sm"
              variant="ghost"
              className="text-amber-300 hover:text-amber-200 text-xs border border-amber-500/20 hover:bg-amber-500/10"
              onClick={() => {
                if (window.confirm('Sincronización completa: descargará TODOS los productos (~30 min). ¿Continuar?')) {
                  handleSync(true);
                }
              }}
              disabled={isSyncing || syncing}
              title="Re-procesar todo el catálogo desde cero (tarda ~30 min)"
            >
              Sync completo
            </Button>
            <Button
              data-testid="ps-force-sync-btn"
              size="sm"
              variant="ghost"
              className="text-orange-300 hover:text-orange-200 text-xs border border-orange-500/20 hover:bg-orange-500/10"
              onClick={() => {
                if (window.confirm('Resync forzado: re-escribe TODOS los productos ignorando el hash de contenido.\n\nÚtil cuando hay títulos, textos o aplicaciones obsoletos en el buscador aunque el sync diario haya pasado.\n\n¿Continuar?')) {
                  handleSync(false, true);
                }
              }}
              disabled={isSyncing || syncing}
              title="Fuerza re-escritura de todos los productos ignorando el content_hash. Corrige textos obsoletos."
            >
              <RefreshCw size={12} className="mr-1.5" /> Forzar resync
            </Button>
            <Button
              data-testid="ps-feed-preview-btn"
              size="sm"
              variant="ghost"
              className="text-blue-300 hover:text-blue-200 text-xs border border-blue-500/20 hover:bg-blue-500/10"
              onClick={() => { setShowFeedModal(true); setFeedData(null); setFeedError(null); setFeedSearchInput(''); loadFeedPreview(); }}
              title="Ver los datos del feed.php de PrestaShop directamente"
            >
              <FileText size={12} className="mr-1.5" /> Ver feed
            </Button>
            <Button
              data-testid="ps-sync-images-btn"
              size="sm"
              variant="ghost"
              className="text-sky-300 hover:text-sky-200 text-xs border border-sky-500/20 hover:bg-sky-500/10"
              onClick={() => handleSyncImages(false)}
              disabled={isSyncing || syncing}
              title="Obtener solo las imágenes que falten (rápido, no toca los productos)"
            >
              <Image size={12} className="mr-1.5" /> Sincronizar imágenes
            </Button>
            {(isSyncing || syncStatus?.images_status === 'syncing') && (
              <Button
                data-testid="ps-cancel-sync-btn"
                size="sm"
                variant="ghost"
                className="text-red-300 hover:text-red-200 text-xs border border-red-500/30 hover:bg-red-500/10"
                onClick={handleCancelSync}
                title="Detener la sincronización en curso (los productos ya importados se conservan)"
              >
                <XCircle size={12} className="mr-1.5" /> Detener
              </Button>
            )}
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-white text-xs"
              onClick={() => setShowForm(true)}
            >
              Reconfigurar
            </Button>
            <Button
              data-testid="ps-diagnose-btn"
              size="sm"
              variant="ghost"
              className="text-amber-300 hover:text-amber-200 text-xs border border-amber-500/20 hover:bg-amber-500/10"
              onClick={handleDiagnose}
              disabled={diagnosing}
              title="Comprueba la IP de salida de este servidor y si el feed es alcanzable (útil para bloqueos de firewall)"
            >
              {diagnosing ? 'Diagnosticando…' : 'Diagnóstico de feed'}
            </Button>
            <button
              onClick={handleDisconnect}
              className="text-xs text-red-400/60 hover:text-red-400 transition-colors ml-auto"
            >
              Desconectar
            </button>
          </>
        )}
      </div>

      {/* Progreso de sincronización de imágenes */}
      {syncStatus?.images_status === 'syncing' && (
        <div data-testid="ps-images-progress" className="flex items-center gap-2 text-xs text-sky-300 mb-2">
          <RefreshCw size={12} className="animate-spin" />
          Sincronizando imágenes: {syncStatus.images_synced || 0}
          {syncStatus.images_total ? ` / ${syncStatus.images_total}` : ''}
        </div>
      )}
      {syncStatus?.images_status === 'blocked' && (
        <div data-testid="ps-images-blocked" className="flex items-center gap-2 text-xs text-amber-400 mb-2">
          <AlertCircle size={12} />
          Imágenes pausadas: el servidor bloqueó las peticiones. Reintenta "Sincronizar imágenes" más tarde.
        </div>
      )}
      {syncStatus?.images_status === 'completed' && syncStatus.images_synced > 0 && (
        <div data-testid="ps-images-done" className="flex items-center gap-2 text-xs text-emerald-400/80 mb-2">
          <CheckCircle size={12} /> {syncStatus.images_synced} imágenes sincronizadas
        </div>
      )}
      {showLangModal && (
        <MultistoreDomainsModal
          onClose={() => setShowLangModal(false)}
          onSaved={onRefresh}
        />
      )}

      {/* ── Feed Preview Modal ──────────────────────────────── */}
      {showFeedModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">

            {/* Header */}
            <div className="flex items-center justify-between px-5 pt-5 pb-3">
              <div>
                <h2 className="text-white font-semibold flex items-center gap-2">
                  <FileText size={16} className="text-blue-400" /> Diagnóstico: Feed vs MongoDB
                </h2>
                <p className="text-gray-500 text-xs mt-0.5">
                  Compara lo que devuelve el feed.php con lo que está almacenado en la base de datos
                </p>
              </div>
              <button onClick={() => setShowFeedModal(false)} className="text-gray-400 hover:text-white ml-4">
                <XIcon size={20} />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-5 border-b border-gray-800">
              {[
                { id: 'feed',    label: 'Feed (PrestaShop)' },
                { id: 'db',      label: 'BD (MongoDB)' },
                { id: 'compare', label: 'Comparar' },
              ].map(tab => (
                <button
                  key={tab.id}
                  data-testid={`feed-modal-tab-${tab.id}`}
                  onClick={() => setFeedModalTab(tab.id)}
                  className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors -mb-px ${
                    feedModalTab === tab.id
                      ? 'border-blue-500 text-white'
                      : 'border-transparent text-gray-500 hover:text-gray-300'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Search bar */}
            <div className="p-4 border-b border-gray-800 flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  data-testid="feed-modal-search-input"
                  type="text"
                  placeholder={
                    feedModalTab === 'feed'    ? 'Buscar por nombre, referencia, aplicación...' :
                    feedModalTab === 'db'      ? 'Referencia, ID externo o fragmento del título...' :
                                                'Referencia o título para comparar ambas fuentes...'
                  }
                  value={feedSearchInput}
                  onChange={e => setFeedSearchInput(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      setFeedSearch(feedSearchInput);
                      if (feedModalTab === 'feed') { loadFeedPreview(feedSearchInput); }
                      else if (feedModalTab === 'db') { loadDbInspect(feedSearchInput); }
                      else { loadFeedPreview(feedSearchInput); loadDbInspect(feedSearchInput); }
                    }
                  }}
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-8 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-blue-500"
                />
              </div>
              <Button
                data-testid="feed-modal-search-btn"
                size="sm"
                onClick={() => {
                  setFeedSearch(feedSearchInput);
                  if (feedModalTab === 'feed') { loadFeedPreview(feedSearchInput); }
                  else if (feedModalTab === 'db') { loadDbInspect(feedSearchInput); }
                  else { loadFeedPreview(feedSearchInput); loadDbInspect(feedSearchInput); }
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                {(feedLoading || dbLoading) ? <RefreshCw size={12} className="animate-spin" /> : <Search size={12} />}
              </Button>
              <Button
                size="sm" variant="ghost"
                onClick={() => {
                  setFeedSearchInput(''); setFeedSearch('');
                  if (feedModalTab !== 'db') loadFeedPreview('');
                  setDbData(null);
                }}
                className="text-gray-400 hover:text-white text-xs"
              >
                Limpiar
              </Button>
            </div>

            {/* Tab content */}
            <div className="flex-1 overflow-y-auto p-4">

              {/* ── Tab: FEED ── */}
              {feedModalTab === 'feed' && (
                <>
                  {feedLoading && (
                    <div className="flex items-center justify-center h-40 text-gray-400">
                      <RefreshCw size={18} className="animate-spin mr-2" /> Descargando feed...
                    </div>
                  )}
                  {feedError && (
                    <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 text-red-300 text-sm">
                      <strong>Error:</strong> {feedError}
                    </div>
                  )}
                  {feedData && !feedLoading && (
                    <>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-gray-400 text-xs">
                          {feedData.returned} producto{feedData.returned !== 1 ? 's' : ''} mostrados
                          {feedSearch && ` (filtrando por "${feedSearch}")`}
                          {!feedSearch && feedData.total_seen > feedData.returned && ` — primeros ${feedData.returned} del feed`}
                        </span>
                        <span className="text-gray-600 text-xs">shop={feedData.shop} lang={feedData.lang}</span>
                      </div>
                      {feedData.products.length === 0 ? (
                        <div className="text-center text-gray-500 py-12">
                          No se encontraron productos {feedSearch ? `con "${feedSearch}"` : ''}
                        </div>
                      ) : (
                        <div className="space-y-2">
                          {feedData.products.map((p, i) => (
                            <div key={i} className="bg-gray-800 rounded-xl p-3 text-sm">
                              <div className="flex items-start gap-3">
                                {p.image_url && (
                                  <img src={p.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0 bg-gray-700" />
                                )}
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-white font-medium">{p.name || '—'}</span>
                                    <span className={`text-xs px-1.5 py-0.5 rounded ${p.active == 1 ? 'bg-emerald-900/50 text-emerald-400' : 'bg-red-900/50 text-red-400'}`}>
                                      {p.active == 1 ? 'Activo' : 'Inactivo'}
                                    </span>
                                  </div>
                                  <div className="text-gray-400 text-xs mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5">
                                    <span>Ref: <span className="text-gray-300">{p.reference || '—'}</span></span>
                                    <span>Precio: <span className="text-gray-300">{p.price_tax_incl ? `${p.price_tax_incl}€` : '—'}</span></span>
                                    <span>Stock: <span className="text-gray-300">{p.quantity ?? '—'}</span></span>
                                    <span>ID: <span className="text-gray-300">{p.id}</span></span>
                                  </div>
                                  {p.application && (
                                    <div className="text-gray-500 text-xs mt-0.5 truncate">
                                      <span className="text-gray-600">Aplicación:</span> {p.application}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Tab: BD (MongoDB) ── */}
              {feedModalTab === 'db' && (
                <>
                  {!feedSearch && !dbData && (
                    <div className="text-center text-gray-500 py-16">
                      <Search size={28} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Escribe una referencia, ID externo o parte del título</p>
                      <p className="text-xs mt-1 text-gray-600">Muestra exactamente lo que hay en MongoDB, sin pasar por Atlas Search</p>
                    </div>
                  )}
                  {dbLoading && (
                    <div className="flex items-center justify-center h-40 text-gray-400">
                      <RefreshCw size={18} className="animate-spin mr-2" /> Consultando MongoDB...
                    </div>
                  )}
                  {dbError && (
                    <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 text-red-300 text-sm">
                      <strong>Error:</strong> {dbError}
                    </div>
                  )}
                  {dbData && !dbLoading && (
                    <>
                      <div className="text-gray-400 text-xs mb-3">
                        {dbData.count} resultado{dbData.count !== 1 ? 's' : ''} en MongoDB{dbData.query ? ` para "${dbData.query}"` : ''}
                      </div>
                      {dbData.products.length === 0 ? (
                        <div className="text-center text-gray-500 py-12">
                          No hay productos en MongoDB con esa búsqueda
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {dbData.products.map((p, i) => {
                            const titleObj = typeof p.title === 'object' ? p.title : {};
                            const defTitle = titleObj[p.default_lang] || Object.values(titleObj)[0] || p.title || '—';
                            return (
                              <div key={i} data-testid={`db-product-row-${i}`} className="bg-gray-800 rounded-xl p-4 text-sm">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                  <span className="text-white font-medium">{defTitle}</span>
                                  <span className="text-gray-500 text-xs font-mono shrink-0">ID: {p.external_id}</span>
                                </div>
                                {Object.keys(titleObj).length > 1 && (
                                  <div className="flex flex-wrap gap-1.5 mb-2">
                                    {Object.entries(titleObj).map(([lang, val]) => (
                                      <span key={lang} className="text-xs bg-gray-700/80 rounded px-2 py-0.5">
                                        <span className="text-gray-500 uppercase">{lang}:</span>{' '}
                                        <span className="text-gray-200">{val}</span>
                                      </span>
                                    ))}
                                  </div>
                                )}
                                <div className="text-gray-500 text-xs flex flex-wrap gap-x-4 gap-y-0.5">
                                  <span>Ref: <span className="text-gray-300 font-mono">{p.reference || '—'}</span></span>
                                  <span>Lang defecto: <span className="text-gray-300">{p.default_lang || '—'}</span></span>
                                  <span>Hash: <span className="font-mono text-gray-600">{p.content_hash || '—'}</span></span>
                                  <span>Sincronizado: <span className="text-gray-300">
                                    {p.synced_at ? new Date(p.synced_at).toLocaleString('es') : '—'}
                                  </span></span>
                                  <span>Activo: <span className={p.is_active ? 'text-emerald-400' : 'text-red-400'}>{p.is_active ? 'Sí' : 'No'}</span></span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}

              {/* ── Tab: COMPARAR ── */}
              {feedModalTab === 'compare' && (
                <>
                  {!feedSearch && !feedData && !dbData && (
                    <div className="text-center text-gray-500 py-16">
                      <Search size={28} className="mx-auto mb-3 opacity-30" />
                      <p className="text-sm">Busca un producto para ver Feed vs MongoDB en paralelo</p>
                      <p className="text-xs mt-1 text-gray-600">Ej: referencia "GRX400" o título "frenos aerox"</p>
                    </div>
                  )}
                  {(feedLoading || dbLoading) && (
                    <div className="flex items-center justify-center h-40 text-gray-400">
                      <RefreshCw size={18} className="animate-spin mr-2" /> Cargando ambas fuentes...
                    </div>
                  )}
                  {feedData && dbData && !feedLoading && !dbLoading && (() => {
                    // Build a map: external_id → db product
                    const dbById = {};
                    const dbByRef = {};
                    (dbData.products || []).forEach(p => {
                      if (p.external_id) dbById[String(p.external_id)] = p;
                      if (p.reference)   dbByRef[String(p.reference)]  = p;
                    });

                    const pairs = (feedData.products || []).map(fp => {
                      const dbMatch = dbById[String(fp.id)] || dbByRef[String(fp.reference)] || null;
                      return { feed: fp, db: dbMatch };
                    });

                    // DB-only (in DB but not in feed results)
                    const feedIds = new Set((feedData.products || []).map(p => String(p.id)));
                    const dbOnly = (dbData.products || []).filter(p => !feedIds.has(String(p.external_id)));

                    return (
                      <div className="space-y-4">
                        {pairs.length === 0 && dbOnly.length === 0 && (
                          <div className="text-center text-gray-500 py-12">No se encontraron productos</div>
                        )}

                        {pairs.map((pair, i) => {
                          const fp = pair.feed;
                          const dp = pair.db;
                          const feedTitle = fp.name || '';
                          const dbTitleObj = dp && typeof dp.title === 'object' ? dp.title : {};
                          const dbDefLang  = dp?.default_lang || 'es';
                          const dbTitle    = dbTitleObj[dbDefLang] || Object.values(dbTitleObj)[0] || dp?.title || '';
                          const titleMatch = feedTitle && dbTitle && feedTitle.trim().toLowerCase() === dbTitle.trim().toLowerCase();
                          const hasMismatch = dp && feedTitle && dbTitle && !titleMatch;

                          return (
                            <div
                              key={i}
                              data-testid={`compare-row-${i}`}
                              className={`rounded-xl border p-4 ${
                                hasMismatch ? 'border-amber-500/40 bg-amber-500/5' :
                                !dp         ? 'border-gray-700 bg-gray-800/40' :
                                              'border-emerald-500/20 bg-gray-800/40'
                              }`}
                            >
                              {/* Status badge */}
                              <div className="flex items-center justify-between mb-3">
                                <span className="text-gray-300 text-xs font-mono">
                                  ID: {fp.id}{fp.reference ? ` · Ref: ${fp.reference}` : ''}
                                </span>
                                {hasMismatch ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 font-medium">
                                    DESINCRONIZADO
                                  </span>
                                ) : !dp ? (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">
                                    Solo en feed (no indexado aún)
                                  </span>
                                ) : (
                                  <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400">
                                    Sincronizado
                                  </span>
                                )}
                              </div>

                              {/* Side by side */}
                              <div className="grid grid-cols-2 gap-3">
                                {/* Feed side */}
                                <div className="bg-gray-900/60 rounded-lg p-3">
                                  <div className="text-xs font-medium text-blue-400 mb-2">Feed (PrestaShop)</div>
                                  <div className={`text-sm font-medium mb-1 ${hasMismatch ? 'text-amber-300' : 'text-white'}`}>
                                    {fp.name || '—'}
                                  </div>
                                  <div className="text-xs text-gray-500 space-y-0.5">
                                    <div>Precio: <span className="text-gray-300">{fp.price_tax_incl ? `${fp.price_tax_incl}€` : '—'}</span></div>
                                    <div>Stock: <span className="text-gray-300">{fp.quantity ?? '—'}</span></div>
                                    {fp.application && <div className="truncate">App: <span className="text-gray-300">{fp.application}</span></div>}
                                  </div>
                                </div>

                                {/* DB side */}
                                <div className="bg-gray-900/60 rounded-lg p-3">
                                  <div className="text-xs font-medium text-purple-400 mb-2">MongoDB</div>
                                  {dp ? (
                                    <>
                                      <div className={`text-sm font-medium mb-1 ${hasMismatch ? 'text-red-300' : 'text-white'}`}>
                                        {dbTitle || '—'}
                                      </div>
                                      {Object.keys(dbTitleObj).length > 1 && (
                                        <div className="flex flex-wrap gap-1 mb-1">
                                          {Object.entries(dbTitleObj).map(([lang, val]) => (
                                            <span key={lang} className="text-[10px] bg-gray-700/80 rounded px-1.5 py-0.5">
                                              <span className="text-gray-500 uppercase">{lang}:</span> <span className="text-gray-300">{val}</span>
                                            </span>
                                          ))}
                                        </div>
                                      )}
                                      <div className="text-xs text-gray-500 space-y-0.5">
                                        <div>Hash: <span className="font-mono text-gray-600">{dp.content_hash || '—'}</span></div>
                                        <div>Sync: <span className="text-gray-300">{dp.synced_at ? new Date(dp.synced_at).toLocaleString('es') : '—'}</span></div>
                                      </div>
                                    </>
                                  ) : (
                                    <div className="text-gray-600 text-xs italic pt-1">No encontrado en MongoDB</div>
                                  )}
                                </div>
                              </div>

                              {/* Mismatch explanation */}
                              {hasMismatch && (
                                <div className="mt-2 text-xs text-amber-400/80 bg-amber-500/5 rounded p-2">
                                  El título en BD difiere del feed. El sync debería haberlo actualizado.
                                  Comprueba que el sync completó correctamente y si el hash cambió.
                                </div>
                              )}
                            </div>
                          );
                        })}

                        {dbOnly.map((dp, i) => {
                          const titleObj = typeof dp.title === 'object' ? dp.title : {};
                          const defTitle = titleObj[dp.default_lang] || Object.values(titleObj)[0] || dp.title || '—';
                          return (
                            <div key={`dbonly-${i}`} className="rounded-xl border border-gray-700 bg-gray-800/30 p-4">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-gray-400 text-xs font-mono">ID: {dp.external_id} · Ref: {dp.reference || '—'}</span>
                                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-700 text-gray-400">Solo en BD</span>
                              </div>
                              <div className="text-gray-300 text-sm">{defTitle}</div>
                              <div className="text-xs text-gray-600 mt-1">Sync: {dp.synced_at ? new Date(dp.synced_at).toLocaleString('es') : '—'}</div>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </>
              )}

            </div>

            {/* Footer */}
            <div className="p-4 border-t border-gray-700 flex justify-between items-center gap-4">
              <p className="text-gray-500 text-xs">
                {feedModalTab === 'compare'
                  ? 'DESINCRONIZADO → el sync no escribió bien en BD. Sincronizado + buscador erróneo → delay de Atlas Search (espera ~15min y prueba de nuevo).'
                  : feedModalTab === 'db'
                  ? 'Datos directos de MongoDB, sin pasar por Atlas Search. Si aquí el título es correcto, es solo un delay del índice.'
                  : 'Si los títulos del feed difieren del buscador → usa "Forzar resync". Si en el feed también están mal → el feed.php tiene caché en PS.'}
              </p>
              <Button size="sm" onClick={() => setShowFeedModal(false)} variant="ghost" className="text-gray-400 shrink-0">
                Cerrar
              </Button>
            </div>

          </div>
        </div>
      )}
    </div>
  );
}

// ── Modal: Gestión de dominios e idiomas por shop ──────────
function MultistoreDomainsModal({ onClose, onSaved }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [shops, setShops] = useState([]);
  const [langMap, setLangMap] = useState({});
  const [defaultIso, setDefaultIso] = useState('');
  const [overrides, setOverrides] = useState({});
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [scheduleHour, setScheduleHour] = useState(4);
  const [lastFullAt, setLastFullAt] = useState(null);
  const [maxProductId, setMaxProductId] = useState(0);
  const [lastScheduledRun, setLastScheduledRun] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          api.get('/api/connectors/prestashop/shop-domains'),
          api.get('/api/connectors/prestashop/schedule'),
        ]);
        if (cancelled) return;
        const d = r1.data;
        setShops(d.shops || []);
        setLangMap(d.lang_map || {});
        setDefaultIso(d.default_iso || '');
        const ovs = {};
        (d.shops || []).forEach(s => {
          ovs[s.id_shop] = s.override_url || s.detected_base_url || '';
        });
        setOverrides(ovs);
        const sch = r2.data;
        setScheduleEnabled(sch.enabled !== false);
        setScheduleHour(typeof sch.hour === 'number' ? sch.hour : 4);
        setLastFullAt(sch.last_full_sync_at || null);
        setMaxProductId(sch.max_product_id || 0);
        setLastScheduledRun(sch.last_scheduled_run || null);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'No se pudo cargar la información');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const toSend = {};
      shops.forEach(s => {
        const val = (overrides[s.id_shop] || '').trim();
        if (val && val !== (s.detected_base_url || '')) {
          toSend[String(s.id_shop)] = val;
        }
      });
      await Promise.all([
        api.put('/api/connectors/prestashop/shop-domains', { overrides: toSend }),
        api.put('/api/connectors/prestashop/schedule', {
          enabled: scheduleEnabled,
          hour: scheduleHour,
        }),
      ]);
      onSaved && onSaved();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  const handleResetShop = (sid) => {
    const s = shops.find(x => String(x.id_shop) === String(sid));
    if (!s) return;
    setOverrides({ ...overrides, [sid]: s.detected_base_url || '' });
  };

  return (
    <div
      data-testid="ps-multistore-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <div
        className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-white font-semibold text-lg">Gestionar tiendas e idiomas</h3>
            <p className="text-gray-500 text-xs mt-1">
              Configura el dominio público de cada tienda y revisa los idiomas detectados.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white text-xl leading-none"
            data-testid="ps-modal-close"
          >×</button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-gray-400 text-sm py-8 justify-center">
            <RefreshCw size={14} className="animate-spin" /> Cargando información…
          </div>
        )}

        {!loading && error && (
          <div className="text-red-400 text-xs bg-red-500/10 border border-red-500/30 rounded p-3">
            {error}
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="mb-5 bg-gray-800/40 border border-gray-700/60 rounded-xl p-3">
              <p className="text-xs font-medium text-gray-300 mb-2">
                Idiomas detectados ({Object.keys(langMap).length})
              </p>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(langMap).map(([lid, iso]) => (
                  <span
                    key={lid}
                    className={`px-2 py-0.5 rounded text-[11px] font-mono uppercase ${
                      iso === defaultIso
                        ? 'bg-emerald-500/15 text-emerald-300'
                        : 'bg-gray-700/60 text-gray-300'
                    }`}
                  >
                    {iso}{iso === defaultIso ? ' ★' : ''} <span className="opacity-50">#{lid}</span>
                  </span>
                ))}
              </div>
            </div>

            <div className="space-y-3 mb-5">
              {shops.map((s) => (
                <div
                  key={s.id_shop}
                  data-testid={`ps-shop-row-${s.id_shop}`}
                  className="bg-gray-800/40 border border-gray-700/60 rounded-xl p-3"
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <span className="font-mono text-gray-500 text-xs shrink-0">#{s.id_shop}</span>
                      <span className="text-gray-200 text-sm truncate">{s.name}</span>
                      {s.default_lang_iso && (
                        <span className="px-1.5 py-0.5 rounded bg-indigo-500/15 text-indigo-300 text-[10px] uppercase font-mono shrink-0">
                          default: {s.default_lang_iso}
                        </span>
                      )}
                    </div>
                    {s.is_overridden && (
                      <span className="px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 text-[10px] shrink-0">
                        override
                      </span>
                    )}
                  </div>

                  {Array.isArray(s.active_lang_isos) && s.active_lang_isos.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-2">
                      {s.active_lang_isos.map((iso) => (
                        <span
                          key={iso}
                          className="px-1.5 py-0.5 rounded bg-gray-700/60 text-gray-400 text-[10px] font-mono uppercase"
                        >
                          {iso}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="space-y-1">
                    <label className="text-[10px] text-gray-500">Dominio público (URL completa)</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        data-testid={`ps-shop-domain-${s.id_shop}`}
                        value={overrides[s.id_shop] || ''}
                        onChange={(e) => setOverrides({ ...overrides, [s.id_shop]: e.target.value })}
                        placeholder={s.detected_base_url || 'https://...'}
                        className="flex-1 bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500"
                      />
                      <button
                        type="button"
                        onClick={() => handleResetShop(s.id_shop)}
                        className="text-[10px] text-gray-500 hover:text-gray-300 px-2"
                        title="Restaurar dominio detectado"
                      >
                        Reset
                      </button>
                    </div>
                    {s.detected_base_url && (
                      <p className="text-[10px] text-gray-600 font-mono">
                        Detectado: {s.detected_base_url}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mb-5 bg-gray-800/40 border border-gray-700/60 rounded-xl p-3">
              <div className="flex items-center justify-between mb-3">
                <p className="text-xs font-medium text-gray-300">Sincronización automática</p>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid="ps-schedule-enabled"
                    checked={scheduleEnabled}
                    onChange={(e) => setScheduleEnabled(e.target.checked)}
                    className="accent-indigo-500"
                  />
                  <span className="text-[11px] text-gray-400">
                    {scheduleEnabled ? 'Activada' : 'Desactivada'}
                  </span>
                </label>
              </div>

              <div className={`space-y-2 ${scheduleEnabled ? '' : 'opacity-50 pointer-events-none'}`}>
                <label className="text-[10px] text-gray-500 block">
                  Hora diaria (Europe/Madrid)
                </label>
                <div className="flex items-center gap-2">
                  <select
                    data-testid="ps-schedule-hour"
                    value={scheduleHour}
                    onChange={(e) => setScheduleHour(parseInt(e.target.value, 10))}
                    className="bg-gray-900 border border-gray-700 rounded px-2 py-1.5 text-xs text-gray-200 font-mono focus:outline-none focus:border-indigo-500"
                  >
                    {Array.from({ length: 24 }, (_, i) => (
                      <option key={i} value={i}>
                        {String(i).padStart(2, '0')}:00
                      </option>
                    ))}
                  </select>
                  <span className="text-[11px] text-gray-500">
                    todos los días — sincronización incremental
                  </span>
                </div>
                {lastScheduledRun && (
                  <p className="text-[10px] text-gray-600">
                    Última ejecución programada: {new Date(lastScheduledRun).toLocaleString('es')}
                  </p>
                )}
                {lastFullAt && (
                  <p className="text-[10px] text-gray-600">
                    Última sincronización completa: {new Date(lastFullAt).toLocaleString('es')}
                  </p>
                )}
                {maxProductId > 0 && (
                  <p className="text-[10px] text-gray-600">
                    Último ID procesado: <span className="font-mono">{maxProductId}</span> (próximo incremental traerá productos con id mayor)
                  </p>
                )}
              </div>
            </div>

            <div className="text-[10px] text-gray-500 bg-gray-800/30 border border-gray-700/40 rounded p-2 mb-4">
              Los cambios se aplican en la próxima sincronización. Si activas el scope <code className="text-gray-400">shop_urls</code> en el webservice de PrestaShop, los dominios se detectan automáticamente.
            </div>

            <div className="flex items-center gap-2 justify-end">
              <Button
                size="sm"
                variant="ghost"
                className="text-gray-400"
                onClick={onClose}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                data-testid="ps-modal-save"
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? <RefreshCw size={12} className="animate-spin mr-1.5" /> : null}
                Guardar overrides
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Shopify Card (OAuth 2.0) ──────────────────────────────────
function ShopifyCard({ data, onRefresh, oauthMsg, setOauthMsg }) {
  const [showForm, setShowForm] = useState(false);
  const [shop, setShop] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState('');

  const isConfigured = data?.configured;
  const syncStatus = data?.sync;
  const isSyncing = syncStatus?.status === 'syncing';

  // Show OAuth result message passed from parent (URL params on load)
  const displayMsg = oauthMsg || msg;
  const isSuccess = oauthMsg?.includes('correctamente') || false;

  const handleOAuthConnect = async () => {
    const domain = shop.trim();
    if (!domain) {
      setMsg('Introduce el dominio de tu tienda (ej: mitienda.myshopify.com)');
      return;
    }
    setConnecting(true);
    setMsg('');
    try {
      const res = await api.get('/api/connectors/shopify/oauth/install', {
        params: { shop: domain },
      });
      // Redirect the browser to Shopify authorization page
      window.location.href = res.data.redirect_url;
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al iniciar la conexión con Shopify');
      setConnecting(false);
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      await api.post('/api/connectors/shopify/sync');
      setMsg('Sincronización iniciada');
      let polls = 0;
      const interval = setInterval(async () => {
        polls++;
        onRefresh();
        if (polls > 60) clearInterval(interval);
      }, 3000);
      setTimeout(() => clearInterval(interval), 300000);
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al sincronizar');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('¿Desconectar Shopify? Esto no borrará los productos indexados.')) return;
    await api.delete('/api/connectors/shopify/disconnect');
    onRefresh();
  };

  return (
    <div data-testid="shopify-card" className="bg-gray-900 border border-gray-800 rounded-2xl p-6">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShopifyIcon size={36} />
          <div>
            <h3 className="text-white font-semibold">Shopify</h3>
            <p className="text-gray-500 text-xs mt-0.5">OAuth 2.0 · GraphQL Admin API</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
          isConfigured ? 'bg-emerald-500/15 text-emerald-400' : 'bg-gray-700 text-gray-400'
        }`}>
          {isConfigured ? <><Plug size={10} /> Conectado</> : <><Unplug size={10} /> No conectado</>}
        </div>
      </div>

      {isConfigured && (
        <div className="mb-4">
          <p className="text-gray-500 text-xs mb-1">Tienda conectada</p>
          <p className="text-gray-300 text-sm font-mono">{data.shop_name || data.shop}</p>
          {data.shop_name && data.shop !== data.shop_name && (
            <p className="text-gray-600 text-xs">{data.shop}</p>
          )}
          <div className="mt-2">
            <SyncBadge status={syncStatus} productCount={data.product_count} />
          </div>
          {syncStatus?.completed_at && (
            <p className="text-gray-600 text-xs mt-1">
              Última sync: {new Date(syncStatus.completed_at).toLocaleString('es')}
            </p>
          )}
        </div>
      )}

      {/* Message (OAuth result or action feedback) */}
      {displayMsg && (
        <div className={`mb-3 text-xs rounded-lg px-3 py-2 border ${
          isSuccess
            ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20'
            : 'text-amber-300 bg-amber-500/10 border-amber-500/20'
        }`} onClick={() => { setMsg(''); setOauthMsg(''); }}>
          {displayMsg}
        </div>
      )}

      {/* OAuth connect form */}
      {showForm && (
        <div data-testid="shopify-oauth-form" className="mb-4 space-y-3 bg-gray-800/50 rounded-xl p-4 border border-gray-700">
          <div>
            <label className="text-gray-400 text-xs block mb-1">Dominio de tu tienda Shopify</label>
            <input
              data-testid="sh-domain-input"
              type="text"
              value={shop}
              onChange={e => setShop(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleOAuthConnect()}
              placeholder="mitienda.myshopify.com"
              className="w-full bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-600 outline-none focus:border-indigo-500"
              disabled={connecting}
              autoFocus
            />
            <p className="text-gray-600 text-xs mt-1">
              Introduce solo el nombre de tu tienda (ej: <span className="text-gray-500">mitienda</span>) o el dominio completo <span className="text-gray-500">.myshopify.com</span>
            </p>
          </div>
          <div className="bg-indigo-500/5 border border-indigo-500/15 rounded-lg p-3 text-xs text-gray-400">
            <p className="font-medium text-indigo-400 mb-1">Flujo OAuth 2.0</p>
            <p>Serás redirigido a Shopify para autorizar el acceso. Sera solo solicitará permisos de <code className="bg-gray-800 px-1 rounded">read_products</code> e <code className="bg-gray-800 px-1 rounded">read_inventory</code>.</p>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="sh-oauth-connect-btn"
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              onClick={handleOAuthConnect}
              disabled={connecting}
            >
              {connecting
                ? <><RefreshCw size={12} className="animate-spin mr-1.5" /> Conectando...</>
                : <><Plug size={12} className="mr-1.5" /> Autorizar con Shopify</>
              }
            </Button>
            <Button size="sm" variant="ghost" className="text-gray-500" onClick={() => { setShowForm(false); setMsg(''); }}>
              Cancelar
            </Button>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2 flex-wrap">
        {!isConfigured ? (
          <Button
            data-testid="sh-configure-btn"
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            onClick={() => setShowForm(v => !v)}
          >
            <Plug size={13} className="mr-1.5" /> {showForm ? 'Cancelar' : 'Conectar con Shopify'}
          </Button>
        ) : (
          <>
            <Button
              data-testid="sh-sync-btn"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={handleSync}
              disabled={isSyncing || syncing}
            >
              {isSyncing ? <RefreshCw size={12} className="animate-spin mr-1.5" /> : <Play size={12} className="mr-1.5" />}
              {isSyncing ? 'Sincronizando...' : 'Sincronizar catálogo'}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-gray-500 hover:text-white text-xs"
              onClick={() => setShowForm(v => !v)}
            >
              Reconectar
            </Button>
            <button
              onClick={handleDisconnect}
              className="text-xs text-red-400/60 hover:text-red-400 transition-colors ml-auto"
            >
              Desconectar
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Signals Panel — behavioral learning ──────────────────────
function SignalsPanel({ hasConnector, onRefreshParent }) {
  const [status, setStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [csvUploading, setCsvUploading] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('info'); // info | success | error
  const [showCsv, setShowCsv] = useState(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/signals/status');
      setStatus(res.data);
      if (res.data.import_status === 'running') {
        setImporting(true);
      } else if (importing && res.data.import_status !== 'running') {
        setImporting(false);
        if (res.data.import_status === 'completed') {
          setMsg(`Importación completada — ${res.data.products_with_signals.toLocaleString()} productos con historial de ventas`);
          setMsgType('success');
        }
      }
    } catch {
      // silent
    }
  }, [importing]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  // Poll while importing
  useEffect(() => {
    if (importing) {
      pollRef.current = setInterval(loadStatus, 2500);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [importing, loadStatus]);

  const handleForceReset = async () => {
    setMsg('');
    try {
      await api.post('/api/signals/reset');
      setImporting(false);
      await loadStatus();
      setMsg('Import reiniciado. Ya puedes volver a importar.');
      setMsgType('info');
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al reiniciar');
      setMsgType('error');
    }
  };

  const handleImportPS = async () => {
    setMsg('');
    setImporting(true);
    try {
      await api.post('/api/signals/import/prestashop');
      setMsg('Importación iniciada — procesando pedidos en segundo plano...');
      setMsgType('info');
    } catch (e) {
      const detail = e.response?.data?.detail || 'Error al iniciar la importación';
      setMsg(detail);
      setMsgType('error');
      setImporting(false);
    }
  };

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvUploading(true);
    setMsg('');
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await api.post('/api/signals/import/csv', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setMsg(`CSV importado: ${res.data.products_updated} productos, ${res.data.total_units.toLocaleString()} unidades vendidas`);
      setMsgType('success');
      await loadStatus();
    } catch (e) {
      setMsg(e.response?.data?.detail || 'Error al procesar el CSV');
      setMsgType('error');
    } finally {
      setCsvUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const progress = status?.import_progress ?? 0;
  const isRunning = status?.import_status === 'running';
  const isStuck = status?.is_stuck === true;
  const hasData = status?.products_with_signals > 0;

  return (
    <div
      data-testid="signals-panel"
      className="bg-gray-900 border border-gray-800 rounded-2xl p-6"
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-purple-500/15 flex items-center justify-center">
            <TrendingUp size={18} className="text-purple-400" />
          </div>
          <div>
            <h3 className="text-white font-semibold">Aprendizaje de comportamiento</h3>
            <p className="text-gray-500 text-xs mt-0.5">
              Sera aprende qué productos convierten mejor para cada búsqueda
            </p>
          </div>
        </div>
        {hasData && (
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-purple-500/15 text-purple-400">
            <CheckCircle size={10} /> Activo
          </div>
        )}
      </div>

      {/* Stats */}
      {hasData && (
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="bg-gray-800/60 rounded-xl p-3 text-center">
            <p className="text-white font-bold text-lg">{(status?.products_with_signals || 0).toLocaleString()}</p>
            <p className="text-gray-500 text-xs mt-0.5">Productos con señal</p>
          </div>
          <div className="bg-gray-800/60 rounded-xl p-3 text-center">
            <p className="text-white font-bold text-lg">{(status?.total_orders_processed || 0).toLocaleString()}</p>
            <p className="text-gray-500 text-xs mt-0.5">Líneas de pedido</p>
          </div>
          <div className="bg-gray-800/60 rounded-xl p-3 text-center">
            <p className="text-white font-bold text-lg">{(status?.products_with_clicks || 0).toLocaleString()}</p>
            <p className="text-gray-500 text-xs mt-0.5">Clics registrados</p>
          </div>
        </div>
      )}

      {/* Progress bar (during import) */}
      {isRunning && (
        <div className="mb-4 space-y-1.5">
          <div className="flex justify-between text-xs text-gray-400">
            <span className="flex items-center gap-1.5">
              {isStuck
                ? <><AlertCircle size={11} className="text-amber-400" /> Import atascado — lleva más de 10 min sin avanzar</>
                : <><RefreshCw size={11} className="animate-spin" /> Importando historial de ventas...</>
              }
            </span>
            <span>{progress}%</span>
          </div>
          <div className="w-full bg-gray-800 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-500 ${isStuck ? 'bg-amber-500' : 'bg-purple-500'}`}
              style={{ width: `${progress}%` }}
            />
          </div>
          {isStuck && (
            <p className="text-amber-400/70 text-xs mt-1">
              El proceso fue interrumpido (ej: recarga del servidor). Pulsa "Forzar reinicio" para desbloquearlo.
            </p>
          )}
        </div>
      )}

      {/* Last import info */}
      {status?.last_import_at && !isRunning && (
        <p className="text-gray-600 text-xs mb-3">
          Última importación: {new Date(status.last_import_at).toLocaleString('es')}
          {status.import_status === 'error' && (
            <span className="text-red-400 ml-2">(error — vuelve a intentarlo)</span>
          )}
        </p>
      )}

      {/* Message */}
      {msg && (
        <div className={`mb-3 text-xs rounded-lg px-3 py-2 border ${
          msgType === 'success'
            ? 'text-emerald-300 bg-emerald-500/10 border-emerald-500/20'
            : msgType === 'error'
            ? 'text-red-300 bg-red-500/10 border-red-500/20'
            : 'text-purple-300 bg-purple-500/10 border-purple-500/20'
        }`}>
          {msg}
        </div>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        {hasConnector && (
          isStuck ? (
            <Button
              data-testid="force-reset-signals-btn"
              size="sm"
              onClick={handleForceReset}
              className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5 text-xs"
            >
              <AlertCircle size={12} /> Forzar reinicio
            </Button>
          ) : (
            <Button
              data-testid="import-signals-btn"
              size="sm"
              disabled={isRunning || importing}
              onClick={handleImportPS}
              className="bg-purple-600 hover:bg-purple-700 text-white gap-1.5 text-xs"
            >
              {isRunning
                ? <><RefreshCw size={12} className="animate-spin" /> Importando...</>
                : <><Download size={12} /> Importar historial de ventas</>
              }
            </Button>
          )
        )}

        <Button
          data-testid="upload-csv-signals-btn"
          size="sm"
          variant="ghost"
          className="text-gray-400 hover:text-white text-xs gap-1.5"
          onClick={() => setShowCsv(v => !v)}
        >
          <Upload size={12} /> Subir CSV de ventas
        </Button>
      </div>

      {/* CSV upload form */}
      {showCsv && (
        <div
          data-testid="csv-upload-form"
          className="mt-4 bg-gray-800/50 border border-gray-700 rounded-xl p-4 space-y-3"
        >
          <p className="text-gray-400 text-xs">
            Sube un CSV con columnas <code className="bg-gray-900 px-1 rounded text-purple-300">product_id</code> y{' '}
            <code className="bg-gray-900 px-1 rounded text-purple-300">units_sold</code>.
            Puedes exportarlo desde tu ERP o plataforma.
          </p>
          <div className="flex items-center gap-2">
            <input
              ref={fileRef}
              type="file"
              accept=".csv,text/csv"
              onChange={handleCsvUpload}
              disabled={csvUploading}
              data-testid="csv-file-input"
              className="text-xs text-gray-400 file:mr-3 file:py-1 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-gray-700 file:text-gray-300 hover:file:bg-gray-600 cursor-pointer"
            />
            {csvUploading && <RefreshCw size={12} className="animate-spin text-purple-400" />}
          </div>
          <a
            href="data:text/csv;charset=utf-8,product_id%2Cunits_sold%0A81%2C47%0A70722%2C12"
            download="sera_signals_template.csv"
            className="inline-flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300"
          >
            <Download size={11} /> Descargar plantilla CSV
          </a>
        </div>
      )}

      {/* Info box */}
      {!hasData && !isRunning && (
        <p className="text-gray-600 text-xs mt-3">
          Importa el historial de ventas para que Sera aprenda automáticamente qué productos
          convierten mejor en cada búsqueda. Los clics del widget se registran automáticamente
          desde hoy.
        </p>
      )}
    </div>
  );
}


// ── XML Feed Card (existing) ──────────────────────────────────
function XmlFeedCard({ productCount }) {
  return (
    <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 opacity-80">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-9 h-9 rounded-lg bg-blue-500/15 flex items-center justify-center">
          <FileText size={18} className="text-blue-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Feed XML</h3>
          <p className="text-gray-500 text-xs">Google Shopping format · Universal</p>
        </div>
        <div className="ml-auto flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-500/15 text-blue-400">
          <CheckCircle size={10} /> Activo
        </div>
      </div>
      <p className="text-gray-500 text-xs">
        {productCount?.toLocaleString() || 0} productos indexados desde el feed XML.{' '}
        <a href="/feed" className="text-indigo-400 hover:text-indigo-300 underline">Gestionar feed</a>
      </p>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────
export default function ConnectorsPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shopifyOauthMsg, setShopifyOauthMsg] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const res = await api.get('/api/connectors/status');
      setStatus(res.data);
    } catch (e) {
      console.error('Error loading connectors', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Handle OAuth callback result from URL params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const shopifyResult = params.get('shopify');
    if (shopifyResult === 'connected') {
      setShopifyOauthMsg('Shopify conectado correctamente');
      window.history.replaceState({}, '', window.location.pathname);
      loadStatus();
    } else if (shopifyResult === 'error') {
      const reason = params.get('reason') || 'error desconocido';
      setShopifyOauthMsg(`Error al conectar con Shopify: ${reason}`);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [loadStatus]);

  useEffect(() => {
    loadStatus();
    // Auto-refresh every 5s if any sync is in progress
    const interval = setInterval(() => {
      if (
        status?.prestashop?.sync?.status === 'syncing' ||
        status?.shopify?.sync?.status === 'syncing'
      ) {
        loadStatus();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [loadStatus, status]);

  return (
    <div className="p-8 max-w-3xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">Conectores</h1>
          <p className="text-gray-400 text-sm mt-0.5">
            Sincroniza tu catálogo de productos para la búsqueda
          </p>
        </div>
        <button
          onClick={loadStatus}
          className="flex items-center gap-1.5 text-gray-500 hover:text-gray-300 text-sm transition-colors"
          data-testid="connectors-refresh-btn"
        >
          <RefreshCw size={14} /> Actualizar
        </button>
      </div>

      {/* Info banner */}
      <div className="mb-6 bg-indigo-500/5 border border-indigo-500/20 rounded-xl p-4 text-xs text-gray-400">
        <p className="flex items-center gap-2">
          <AlertCircle size={13} className="text-indigo-400 flex-shrink-0" />
          Los productos sincronizados se indexan automáticamente en el motor de búsqueda de Sera. Puedes combinar varias fuentes.
        </p>
      </div>

      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-40 bg-gray-800 rounded-2xl animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="space-y-4" data-testid="connectors-list">
          <PrestashopCard data={status?.prestashop} onRefresh={loadStatus} />
          <ShopifyCard
            data={status?.shopify}
            onRefresh={loadStatus}
            oauthMsg={shopifyOauthMsg}
            setOauthMsg={setShopifyOauthMsg}
          />
          <XmlFeedCard productCount={status?.xml_feed?.product_count} />
          <SignalsPanel
            hasConnector={!!(status?.prestashop?.configured || status?.shopify?.configured)}
            onRefreshParent={loadStatus}
          />
          <EmailConfigSection />
          <SupportEmailConfigSection />
          <MarketingIntegrationsSection />
        </div>
      )}
    </div>
  );
}

